import type { ReactNode } from 'react';

export interface ImpersonationBannerProps {
  /** saas-admin の uid（claims.impersonatedBy） */
  impersonatedBy: string;
  /** Custom Token に同梱された運用上の期限（ISO8601 / JST） */
  expiresAt: string | null;
  /** 「戻る」ボタンクリック時のハンドラ。実装は signOut + location 遷移 */
  onReturn: () => void;
  /** 戻り先 URL（表示のみ。クリック時は onReturn が動く） */
  returnTo: string;
  /** 表示のためのスロット（テストで stub にも使える） */
  children?: ReactNode;
}

/**
 * S-07: tenant-portal で impersonation セッション中に表示する banner。
 *
 * - 注意色の帯（S-136: v2 の warning の面。旧 紫帯）で「[saas-admin] からの impersonation」を明示
 * - 期限 (expiresAt) を読みやすい JST で併記
 * - 「saas-admin に戻る」ボタンで signOut + 遷移
 * - 閲覧専用 (readOnly) であることを明示
 *
 * Tailwind 有り（tenant-portal は導入済）。
 */
export function ImpersonationBanner({
  impersonatedBy,
  expiresAt,
  onReturn,
  returnTo,
  children,
}: ImpersonationBannerProps) {
  return (
    <div
      role="alert"
      aria-label="impersonation セッション"
      className="mb-4 flex flex-col gap-2 rounded-lg bg-warning-tint px-[18px] py-3 text-warning-ink sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex flex-col gap-0.5">
        <p className="text-body font-medium">
          [saas-admin] からの impersonation セッション（閲覧専用）
        </p>
        <p className="text-label">
          actor: <span className="ui-num">{impersonatedBy}</span>
          {expiresAt ? (
            <>
              {' '}
              ・ 期限: <span className="ui-num tabular-nums">{expiresAt}</span>
            </>
          ) : null}
        </p>
        {children}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="hidden text-label sm:inline" aria-hidden>
          戻り先: <span className="ui-num">{returnTo}</span>
        </span>
        <button
          type="button"
          onClick={onReturn}
          className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-1.5 text-label font-medium text-white hover:opacity-90"
        >
          saas-admin に戻る
        </button>
      </div>
    </div>
  );
}
